import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Shield,
  Mail,
  Clock,
  RefreshCw,
  ArrowLeft,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import api from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

const TwoFactorVerification = () => {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [remainingTime, setRemainingTime] = useState(300); // 5 minutes in seconds
  const [remainingAttempts, setRemainingAttempts] = useState(3);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);
  const { dispatch } = useAuth(); // Get dispatch from AuthContext

  // Get user data from location state
  const { userId, email, sentTo, expiresAt } = location.state || {};

  useEffect(() => {
    // Redirect if no user data
    if (!userId) {
      toast.error("Session expired. Please login again.");
      navigate("/login");
      return;
    }

    // Calculate initial remaining time
    if (expiresAt) {
      const expiry = new Date(expiresAt);
      const now = new Date();
      const seconds = Math.max(0, Math.floor((expiry - now) / 1000));
      setRemainingTime(seconds);
    }

    // Focus first input
    inputRefs.current[0]?.focus();
  }, [userId, expiresAt, navigate]);

  // Countdown timer
  useEffect(() => {
    if (remainingTime <= 0) return;

    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setError("Verification code has expired. Please request a new code.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [remainingTime]);

  // Fetch 2FA status periodically
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await api.get(
          `/auth/login/2fa/status?userId=${userId}`,
        );
        if (response.data.success && response.data.data.hasCode) {
          setRemainingTime(response.data.data.remainingSeconds);
          setRemainingAttempts(response.data.data.remainingAttempts);
        }
      } catch (error) {
        console.error("Failed to fetch 2FA status:", error);
      }
    };

    if (userId) {
      fetchStatus();
      const interval = setInterval(fetchStatus, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [userId]);

  const handleInputChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError("");

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (newCode.every((digit) => digit !== "") && !isVerifying) {
      handleVerify(newCode.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle paste
    if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const digits = text.replace(/\D/g, "").slice(0, 6).split("");
        const newCode = [...code];
        digits.forEach((digit, i) => {
          if (i < 6) newCode[i] = digit;
        });
        setCode(newCode);
        if (digits.length === 6) {
          handleVerify(newCode.join(""));
        }
      });
    }
  };

  const handleVerify = async (codeString = code.join("")) => {
    if (codeString.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    if (remainingTime <= 0) {
      setError("Verification code has expired. Please request a new code.");
      return;
    }

    setIsVerifying(true);
    setError("");

    try {
      const response = await api.post("/auth/login/2fa/verify", {
        userId,
        code: codeString,
        remember_me: location.state?.remember_me || false,
      });

      if (response.data.success) {
        // Store auth data
        const { accessToken, refreshToken, user } = response.data.data;
        localStorage.setItem("token", accessToken);
        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
        }
        localStorage.setItem("user", JSON.stringify(user));

        // Update AuthContext
        dispatch({
          type: "LOGIN_SUCCESS",
          payload: {
            user,
            token: accessToken,
            refreshToken: refreshToken || null,
            permissions: user?.UserType?.sidebar_modules || null,
          },
        });

        toast.success("Login successful!");

        // Small delay to ensure state is updated
        setTimeout(() => {
          // Navigate to dashboard
          const from = location.state?.from || "/self-service";
          navigate(from, { replace: true });
        }, 100);
      }
    } catch (error) {
      console.error("2FA verification error:", error);

      const errorMessage =
        error.response?.data?.message ||
        "Verification failed. Please try again.";
      setError(errorMessage);

      // Update remaining attempts
      if (error.response?.data?.remainingAttempts !== undefined) {
        setRemainingAttempts(error.response.data.remainingAttempts);
      }

      // Clear code inputs
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();

      toast.error(errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setError("");

    try {
      const response = await api.post("/auth/login/2fa/resend", { userId });

      if (response.data.success) {
        const { expiresAt } = response.data.data;
        const expiry = new Date(expiresAt);
        const now = new Date();
        const seconds = Math.max(0, Math.floor((expiry - now) / 1000));
        setRemainingTime(seconds);
        setRemainingAttempts(3);
        setCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();

        toast.success("New verification code sent!");
      }
    } catch (error) {
      console.error("Resend error:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to resend code. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 to-purple-700/90 z-10"></div>
        <img
          src="/login.jpeg"
          alt="Login Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 flex flex-col justify-center items-center text-white p-12">
          <div className="max-w-md">
            <Shield className="h-20 w-20 mb-6 text-white" />
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              Secure
              <br />
              Verification
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              We've sent a verification code to your notification email to
              ensure it's really you.
            </p>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-300" />
                <span className="text-lg">Enhanced Security</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-300" />
                <span className="text-lg">Account Protection</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-300" />
                <span className="text-lg">Quick & Easy</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Verification Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="max-w-md w-full">
          {/* Back Button */}
          <button
            onClick={handleBackToLogin}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </button>

          {/* Logo/Brand */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100">
                <Shield className="h-16 w-16 text-blue-600" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Two-Factor Authentication
            </h2>
            <p className="text-gray-600">Enter the 6-digit code sent to</p>
            <p className="text-blue-600 font-medium mt-1">
              {sentTo || "your notification email"}
            </p>
          </div>

          {/* Timer Display */}
          <div className="flex items-center justify-center mb-6">
            <div
              className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium ${
                remainingTime > 60
                  ? "bg-green-50 text-green-700"
                  : "bg-orange-50 text-orange-700"
              }`}
            >
              <Clock className="h-4 w-4" />
              <span>Expires in {formatTime(remainingTime)}</span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-red-700">{error}</p>
                {remainingAttempts > 0 && (
                  <p className="text-xs text-red-600 mt-1">
                    {remainingAttempts}{" "}
                    {remainingAttempts === 1 ? "attempt" : "attempts"} remaining
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Code Input */}
          <div className="mb-8">
            <div className="flex justify-center space-x-3">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={isVerifying || remainingTime <= 0}
                  className={`w-14 h-14 text-center text-2xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                    digit
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-300 hover:border-gray-400"
                  } ${
                    isVerifying || remainingTime <= 0
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Verify Button */}
          <button
            onClick={() => handleVerify()}
            disabled={isVerifying || code.some((d) => !d) || remainingTime <= 0}
            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] mb-4"
          >
            {isVerifying ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Verifying...
              </>
            ) : (
              <>
                <Shield className="h-5 w-5 mr-2" />
                Verify Code
              </>
            )}
          </button>

          {/* Resend Code */}
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">
              Didn't receive the code?
            </p>
            <button
              onClick={handleResend}
              disabled={isResending || remainingTime > 240} // Can resend after 1 minute
              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${isResending ? "animate-spin" : ""}`}
              />
              {isResending ? "Sending..." : "Resend Code"}
            </button>
            {remainingTime > 240 && (
              <p className="text-xs text-gray-500 mt-1">
                Available in {formatTime(remainingTime - 240)}
              </p>
            )}
          </div>

          {/* Security Notice */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-900 mb-1">
                  Security Notice
                </h3>
                <p className="text-xs text-blue-700">
                  This code was sent to your notification email address. Never
                  share this code with anyone, including Puffin ERP staff.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500">
              © {new Date().getFullYear()} Puffin ERP. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorVerification;
